import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { resolveApiUrl } from '../../../environments/resolve-api-url';

export interface ApiRequestOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: any };
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
  observe?: 'body' | 'events' | 'response';
}

export interface GraphQLRequest {
  query: string;
  variables?: { [key: string]: any };
}

/** Response from POST /profile/upload – single url or urls array */
export interface ProfileUploadResponse {
  data?: { url?: string; urls?: string[] };
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = resolveApiUrl();
  }

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  /** Upload file(s) to Azure Blob. Form key: "file". Do not set Content-Type – browser sets multipart boundary. */
  uploadFile(formData: FormData): Observable<ProfileUploadResponse> {
    return this.http.post<ProfileUploadResponse>(`${this.baseUrl}/profile/upload`, formData);
  }

  get<T>(endpoint: string, options?: ApiRequestOptions): Observable<T> {
    if (options?.responseType && options.responseType !== 'json') {
      const requestOptions: any = {
        headers: options?.headers,
        params: options?.params,
        responseType: options.responseType
      };
      return (this.http.get(`${this.baseUrl}/${endpoint}`, requestOptions) as unknown as Observable<T>).pipe(
        catchError(this.handleError)
      );
    }

    return this.http.get<T>(`${this.baseUrl}/${endpoint}`, {
      headers: options?.headers,
      params: options?.params
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * JSON GET that emits `null` when the server responds with 404 (resource not created yet).
   * Other errors use the same handling as {@link ApiService.get}.
   */
  getOrNullOnNotFound<T>(endpoint: string, options?: ApiRequestOptions): Observable<T | null> {
    if (options?.responseType && options.responseType !== 'json') {
      return this.get<T>(endpoint, options);
    }
    return this.http.get<T>(`${this.baseUrl}/${endpoint}`, {
      headers: options?.headers,
      params: options?.params
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(null);
        }
        return this.handleError(error);
      })
    );
  }

  /**
   * GET with full response + Blob body (e.g. sonogram file bytes served as image/jpeg or application/pdf).
   * Avoids default JSON parse on binary payloads.
   */
  getBlobResponse(endpoint: string): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.baseUrl}/${endpoint}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(catchError(this.handleError)) as Observable<HttpResponse<Blob>>;
  }

  post<T>(endpoint: string, body: any, options?: ApiRequestOptions): Observable<T> {
    if (options?.observe === 'response') {
      return (this.http.post(`${this.baseUrl}/${endpoint}`, body, {
        headers: options?.headers,
        params: options?.params,
        observe: 'response'
      }) as unknown as Observable<T>).pipe(
        catchError(this.handleError)
      );
    }

    if (options?.responseType && options.responseType !== 'json') {
      const requestOptions: any = {
        headers: options?.headers,
        params: options?.params,
        responseType: options.responseType
      };
      return (this.http.post(`${this.baseUrl}/${endpoint}`, body, requestOptions) as unknown as Observable<T>).pipe(
        catchError(this.handleError)
      );
    }

    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, body, {
      headers: options?.headers,
      params: options?.params
    }).pipe(
      catchError(this.handleError)
    );
  }

  put<T>(endpoint: string, body: any, options?: ApiRequestOptions): Observable<T> {
    if (options?.responseType && options.responseType !== 'json') {
      const requestOptions: any = {
        headers: options?.headers,
        params: options?.params,
        responseType: options.responseType
      };
      return (this.http.put(`${this.baseUrl}/${endpoint}`, body, requestOptions) as unknown as Observable<T>).pipe(
        catchError(this.handleError)
      );
    }

    return this.http.put<T>(`${this.baseUrl}/${endpoint}`, body, {
      headers: options?.headers,
      params: options?.params
    }).pipe(
      catchError(this.handleError)
    );
  }

  patch<T>(endpoint: string, body: any, options?: ApiRequestOptions): Observable<T> {
    if (options?.responseType && options.responseType !== 'json') {
      const requestOptions: any = {
        headers: options?.headers,
        params: options?.params,
        responseType: options.responseType
      };
      return (this.http.patch(`${this.baseUrl}/${endpoint}`, body, requestOptions) as unknown as Observable<T>).pipe(
        catchError(this.handleError)
      );
    }

    return this.http.patch<T>(`${this.baseUrl}/${endpoint}`, body, {
      headers: options?.headers,
      params: options?.params
    }).pipe(
      catchError(this.handleError)
    );
  }

  delete<T>(endpoint: string, body?: any, options?: ApiRequestOptions): Observable<T> {
    // If body is provided, use http.request with DELETE method
    // Angular's http.delete doesn't support body directly
    if (body !== undefined) {
      const requestOptions: {
        headers?: HttpHeaders | { [header: string]: string | string[] };
        params?: HttpParams | { [param: string]: any };
        body?: any;
        observe: 'body';
        responseType?: 'json';
      } = {
        headers: options?.headers,
        params: options?.params,
        body: body,
        observe: 'body'
      };
      
      if (options?.responseType) {
        requestOptions.responseType = options.responseType as 'json';
      }
      
      return this.http.request<T>('DELETE', `${this.baseUrl}/${endpoint}`, requestOptions).pipe(
        catchError(this.handleError)
      );
    }
    
    // If no body, use standard delete method
    if (options?.responseType && options.responseType !== 'json') {
      const requestOptions: any = {
        headers: options?.headers,
        params: options?.params,
        responseType: options.responseType
      };
      return (this.http.delete(`${this.baseUrl}/${endpoint}`, requestOptions) as unknown as Observable<T>).pipe(
        catchError(this.handleError)
      );
    }

    return this.http.delete<T>(`${this.baseUrl}/${endpoint}`, {
      headers: options?.headers,
      params: options?.params
    }).pipe(
      catchError(this.handleError)
    );
  }

  /** Explicit HTTP DELETE (method visible in DevTools; used for Delphi visit delete). */
  requestDelete<T>(endpoint: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.request<T>('DELETE', `${this.baseUrl}/${endpoint}`, {
      headers: options?.headers,
      params: options?.params,
      observe: 'body',
      responseType: 'json'
    }).pipe(
      catchError(this.handleError)
    );
  }

  graphql<T>(query: string, variables?: { [key: string]: any }, options?: ApiRequestOptions): Observable<T> {
    const graphqlEndpoint = this.baseUrl.endsWith('/graphql') ? this.baseUrl : `${this.baseUrl}/graphql`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...(options?.headers as any)
    });

    const body: GraphQLRequest = {
      query,
      variables
    };

    const requestOptions: any = {
      headers,
      params: options?.params
    };

    if (options?.responseType && options.responseType !== 'json') {
      requestOptions.responseType = options.responseType;
    }

    console.log('=== GraphQL API Request ===');
    console.log('Endpoint:', graphqlEndpoint);
    console.log('Body:', JSON.stringify(body, null, 2));
    console.log('Headers:', headers.keys());
    console.log('==========================');

    return this.http.post<any>(graphqlEndpoint, body, requestOptions).pipe(
      map((response: any) => {
        console.log('=== GraphQL API Response ===');
        console.log('Response:', response);
        console.log('============================');
        if (response?.errors?.length) {
          const msg = response.errors[0]?.message || 'GraphQL request failed';
          throw new Error(msg);
        }
        if (response && response.data) {
          return response.data;
        }
        return response;
      }),
      catchError((error) => {
        console.error('=== GraphQL API Error ===');
        console.error('Error:', error);
        console.error('Error Status:', error?.status);
        console.error('Error StatusText:', error?.statusText);
        console.error('Error Message:', error?.message);
        console.error('Error URL:', error?.url);
        console.error('Error Error:', error?.error);
        console.error('==========================');
        return this.handleError(error);
      })
    ) as Observable<T>;
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      if (error.error?.error?.message) {
        errorMessage = error.error.error.message;
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      }
    }

    return throwError(() => new Error(errorMessage));
  };
}
